import { MIME_TYPES, toBrandedType } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { getSyncableElements } from ".";

import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { Socket } from "socket.io-client";

const HONO_BACKEND_URL = import.meta.env.VITE_APP_HONO_BACKEND_URL || "";

class SceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => {
    return SceneVersionCache.cache.get(socket);
  };
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    SceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

export const loadFirebaseStorage = async () => {
  return null;
};

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);
    return SceneVersionCache.get(portal.socket) === sceneVersion;
  }
  return true;
};

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  if (!HONO_BACKEND_URL) {
    return { savedFiles: files.map((f) => f.id), erroredFiles: [] };
  }

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const response = await fetch(`${HONO_BACKEND_URL}/api/files/upload`, {
          method: "POST",
          headers: {
            "Content-Type": "application/octet-stream",
            "X-File-Id": id,
            "X-Prefix": prefix,
          },
          body: buffer as unknown as BodyInit,
        });
        if (response.ok) {
          savedFiles.push(id);
        } else {
          erroredFiles.push(id);
        }
      } catch (error) {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (!roomId || !roomKey || !socket || isSavedToFirebase(portal, elements)) {
    return null;
  }

  const storedElements = getSyncableElements(elements);
  SceneVersionCache.set(socket, storedElements);

  if (HONO_BACKEND_URL) {
    try {
      const json = JSON.stringify(elements);
      const encoded = new TextEncoder().encode(json);
      const { encryptedBuffer, iv } = await encryptData(roomKey, encoded);

      await fetch(`${HONO_BACKEND_URL}/api/rooms/${roomId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sceneVersion: getSceneVersion(elements),
          ciphertext: Array.from(new Uint8Array(encryptedBuffer)),
          iv: Array.from(iv),
        }),
      });
    } catch (error) {
      console.error("Failed to save room to Hono backend", error);
    }
  }

  return toBrandedType<RemoteExcalidrawElement[]>(storedElements);
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  if (!HONO_BACKEND_URL) {
    return null;
  }

  try {
    const response = await fetch(`${HONO_BACKEND_URL}/api/rooms/${roomId}`);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    if (!data?.ciphertext || !data?.iv) {
      return null;
    }

    const ciphertext = new Uint8Array(data.ciphertext);
    const iv = new Uint8Array(data.iv);
    const decrypted = await decryptData(iv, ciphertext, roomKey);
    const decoded = new TextDecoder("utf-8").decode(new Uint8Array(decrypted));
    const parsedElements = JSON.parse(decoded);

    const elements = getSyncableElements(
      restoreElements(parsedElements, null, { deleteInvisibleElements: true }),
    );

    if (socket) {
      SceneVersionCache.set(socket, elements);
    }

    return elements;
  } catch (error) {
    console.error("Failed to load room from Hono backend", error);
    return null;
  }
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  if (!HONO_BACKEND_URL) {
    return { loadedFiles, erroredFiles };
  }

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const response = await fetch(
          `${HONO_BACKEND_URL}/api/files/${encodeURIComponent(
            prefix.replace(/^\//, ""),
          )}/${id}`,
        );
        if (response.ok) {
          const arrayBuffer = await response.arrayBuffer();
          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            { decryptionKey },
          );
          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error) {
        erroredFiles.set(id, true);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
